import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';

export type Urgency = 'calm' | 'nudge' | 'urgent' | 'expired';

export interface TrialIntelligence {
  isOnTrial: boolean;
  daysRemaining: number;
  trialEndsAt: Date | null;
  canExtend: boolean;
  hasExtended: boolean;
  message: string;
  subMessage: string;
  urgency: Urgency;
  extendTrial: () => Promise<void>;
  shouldShow: boolean;
}

function daysBetween(a: Date, b: Date): number {
  return Math.ceil((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

export function useTrialIntelligence(
  percentage: number,
  needsAttentionCount: number,
): TrialIntelligence {
  const business = useAuthStore(s => s.business);
  const businessId = business?.id;
  const mountedRef = useRef(true);
  const [extending, setExtending] = useState(false);
  const [trialExtended, setTrialExtended] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Fetch trial_extended separately — column may not exist if migration not applied
  useEffect(() => {
    if (!businessId) return;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- pre-existing; IIFE in effect
    (async () => {
      try {
        const { data } = await supabase
          .from('businesses')
          .select('trial_extended')
          .eq('id', businessId)
          .maybeSingle();
        if (mountedRef.current && data?.trial_extended != null) {
          setTrialExtended(data.trial_extended);
        }
      } catch {
        // Column may not exist yet — silently default to false
      }
    })();
  }, [businessId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing; trialEndsAt is derived state, useCallback deps stable across renders
  const trialEndsAt = business?.trial_ends_at
    ? new Date(business.trial_ends_at)
    : null;
  const now = new Date();
  const isOnTrial = trialEndsAt ? trialEndsAt > now : false;
  const daysRemaining = trialEndsAt ? Math.max(0, daysBetween(now, trialEndsAt)) : 0;
  const dayInTrial = 14 - daysRemaining;
  const hasExtended = trialExtended;

  const canExtend = isOnTrial
    && percentage >= 80
    && dayInTrial >= 10
    && !hasExtended;

  let message = '';
  let subMessage = '';
  let urgency: Urgency = 'calm';
  let shouldShow = false;

  if (isOnTrial) {
    shouldShow = true;

    if (dayInTrial <= 6) {
      if (percentage < 50) {
        urgency = 'calm';
        message = "Welcome! Let's get you set up.";
        subMessage = `Your trial runs for ${daysRemaining} more days. Complete your setup to get the most out of it.`;
      } else {
        urgency = 'calm';
        message = `Great progress \u2014 ${percentage}% configured.`;
        subMessage = `${daysRemaining} days left in your trial. Keep going!`;
      }
    } else if (dayInTrial <= 11) {
      if (canExtend) {
        urgency = 'nudge';
        message = `You're ${percentage}% set up. Need more time?`;
        subMessage = 'We can extend your trial by 7 days so you can start using what you\'ve built.';
      } else if (percentage < 60) {
        urgency = 'nudge';
        message = `${needsAttentionCount} item${needsAttentionCount !== 1 ? 's' : ''} still need your attention.`;
        subMessage = `Your trial ends in ${daysRemaining} days. Finish setting up to use everything.`;
      } else {
        urgency = 'nudge';
        message = `Almost there \u2014 ${percentage}% set up.`;
        subMessage = `${daysRemaining} days left. Wrap up the remaining items.`;
      }
    } else if (dayInTrial <= 13) {
      if (canExtend) {
        urgency = 'nudge';
        message = `You're ${percentage}% set up. Need more time?`;
        subMessage = 'We can extend your trial by 7 days so you can start using what you\'ve built.';
      } else {
        urgency = 'urgent';
        message = `Your trial ends in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}.`;
        subMessage = `${needsAttentionCount} item${needsAttentionCount !== 1 ? 's' : ''} need attention before your access changes.`;
      }
    } else {
      urgency = 'urgent';
      message = 'Last day of your trial.';
      subMessage = "Upgrade to keep everything you've built.";
    }
  } else if (!isOnTrial && trialEndsAt) {
    // Post-trial
    if (business?.tier === 'business' || business?.tier === 'pro') {
      // Paid — only show if not fully set up
      if (percentage < 100) {
        shouldShow = true;
        urgency = 'calm';
        message = `${percentage}% configured.`;
        subMessage = `${needsAttentionCount} item${needsAttentionCount !== 1 ? 's' : ''} left to complete your setup.`;
      }
    } else {
      shouldShow = true;
      urgency = 'expired';
      message = "You're on the Free plan.";
      subMessage = 'Upgrade to access the admin panel and all Business features.';
    }
  } else {
    // No trial info, paid user — show progress if < 100%
    if (percentage < 100) {
      shouldShow = true;
      urgency = 'calm';
      message = `${percentage}% configured.`;
      subMessage = `${needsAttentionCount} item${needsAttentionCount !== 1 ? 's' : ''} left to complete your setup.`;
    }
  }

  const extendTrial = useCallback(async () => {
    if (!businessId || !canExtend || extending) return;
    setExtending(true);
    try {
      const newEnd = new Date(trialEndsAt!);
      newEnd.setDate(newEnd.getDate() + 7);

      const { error } = await supabase
        .from('businesses')
        .update({
          trial_ends_at: newEnd.toISOString(),
          trial_extended: true,
          trial_extended_at: new Date().toISOString(),
        })
        .eq('id', businessId);

      if (error) throw error;
      if (mountedRef.current) setTrialExtended(true);
    } catch {
      // Silent — user can try again
    } finally {
      if (mountedRef.current) setExtending(false);
    }
  }, [businessId, canExtend, extending, trialEndsAt]);

  return {
    isOnTrial,
    daysRemaining,
    trialEndsAt,
    canExtend,
    hasExtended,
    message,
    subMessage,
    urgency,
    extendTrial,
    shouldShow,
  };
}
